import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { beginChoreographyAuthorization } from '../../services/choreographyAuth';
import {
  EXPORT_FAILED_MESSAGE, MORE_OPTIONS_LABEL, RECOVERY_COPY, RECOVERY_COPY_STALE_QUEUE_BODY, RECOVERY_COPY_STORAGE_DEGRADED_BODY,
  RECOVERY_STATES, recoveryStateFor,
} from './collaborationRecovery';
import { lex, serializeTokens } from '../../lib/annotation/annotationLexer';
import { ensureNodeId } from '../../lib/annotation/annotationId';
import { concatBoth } from '../annotation/ResolvePicker';
import AnnotationPicker from '../annotation/AnnotationPicker';

// The same download mechanics the old panel used, wrapped so a failure is
// observable: the compound primaries below must confirm the copy exists
// BEFORE anything is cleared, and stop with the queue intact when it does not.
export function downloadRecoveryText(text, routineId) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  try {
    const link = document.createElement('a');
    link.href = url; link.download = `choreo-recovery-${routineId}.txt`; link.click();
  } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}

// Guided recovery surface (Mantis #1128, T-MCRO-024, MCRO001-D004 §4.1).
// Renders ONLY owner-approved copy selected by recovery.state — never
// recovery.message, which stays internal for telemetry. Compound actions keep
// the never-lose-text guarantee: export/copy is confirmed first, then the
// local queue is cleared, then the editor resyncs; an export failure stops the
// whole action with an inline error and the queue untouched.
// MCRO001-F0050 / T-MCRO-050 (D004 §9): the inline conflict-resolution surface.
// A recovery whose UNSENDABLE state carries `recovery.merge` (with conflicts) is
// rendered as the merged document — plain text between boxes, one AnnotationPicker
// per `[!!conflict …-!!]` box (parsed back with the slice-A lexer). Each box is
// resolved ours/theirs/both/hand-edit; when EVERY box is resolved the merged text
// is finalised (each box replaced by its resolution) and submitted, returning the
// editor to live. Read-only / viewer-tier sessions (D004 §5.3) can never resolve.
function MergeResolution({ mergedSource, readOnly, onResolved, mintId }) {
  // Parse the annotated merge into a token stream ONCE; each conflict node gets a
  // stable id minted here (the lexer leaves ids empty to stay pure).
  const tokens = useMemo(() => lex(mergedSource || '').map((token) => (
    token && token.type === 'text' ? token : { ...token, id: ensureNodeId(token, mintId) }
  )), [mergedSource, mintId]);
  const conflictIds = useMemo(() => tokens.filter((t) => !(t && t.type === 'text')).map((t) => t.id), [tokens]);
  const [resolutions, setResolutions] = useState({});

  const onResolve = useCallback((id, resolution) => {
    setResolutions((current) => ({ ...current, [id]: resolution }));
  }, []);

  const allResolved = conflictIds.length > 0 && conflictIds.every((id) => resolutions[id]);

  // Build the finalised text: each conflict node replaced by its chosen side.
  const finalise = useCallback(() => serializeTokens(tokens.map((token) => {
    if (token && token.type === 'text') return token;
    const resolution = resolutions[token.id];
    const text = resolution?.choice === 'ours' ? (token.ours ?? '')
      : resolution?.choice === 'theirs' ? (token.theirs ?? '')
        : resolution?.choice === 'both' ? (resolution.text ?? concatBoth(token.ours, token.theirs))
          : resolution?.choice === 'edit' ? (resolution.text ?? '')
            : (token.ours ?? '');
    return { type: 'text', text };
  })), [tokens, resolutions]);

  return <div className="mc-collab-recovery-merge">
    <div className="mc-collab-recovery-merge-doc">
      {tokens.map((token, index) => (token && token.type === 'text'
        ? <pre key={`t${index}`} className="mc-collab-recovery-merge-text">{token.text}</pre>
        : <AnnotationPicker key={token.id} node={token} onResolve={onResolve} readOnly={readOnly} mintId={mintId} />))}
    </div>
    {!readOnly && <button
      type="button"
      className="mc-collab-recovery-merge-submit"
      disabled={!allResolved}
      onClick={() => onResolved(finalise())}
    >Save the merged version</button>}
  </div>;
}

export default function CollaborativeRecovery({
  recovery,
  recoveryText,
  routineId,
  onDiscard,
  onRetryNow,
  onResolveMerge = () => {},
  onAttemptMerge,
  readOnly = false,
  onSignIn = () => beginChoreographyAuthorization(),
  exportText = downloadRecoveryText,
  copyText = (text) => navigator.clipboard.writeText(text),
  reloadPage = () => window.location.reload(),
  mintId,
}) {
  const state = recovery?.state || recoveryStateFor(recovery?.code);
  const baseCopy = RECOVERY_COPY[state] || RECOVERY_COPY[RECOVERY_STATES.UNSENDABLE];
  // #17: within the UNSENDABLE tier, three distinct root causes read with
  // three distinct bodies — title/primary/helper/options stay identical either
  // way (same merge-first action, same never-lose-text safety net):
  //   1. recovery.storageDegraded — THIS browser's own storage is failing
  //      (requirement 5(c)), checked first: if storage itself is broken, that
  //      is the most actionable thing to tell the person, regardless of what
  //      else may also be true.
  //   2. recovery.staleQueue — a persisted local queue that cannot be
  //      replayed onto the current document (no concurrent editor implied).
  //   3. neither — the owner-approved default: a genuine concurrent-edit
  //      conflict ("someone else made changes at the same time").
  const unsendableBody = state !== RECOVERY_STATES.UNSENDABLE ? null
    : recovery?.storageDegraded ? RECOVERY_COPY_STORAGE_DEGRADED_BODY
      : recovery?.staleQueue ? RECOVERY_COPY_STALE_QUEUE_BODY : null;
  const copy = unsendableBody ? { ...baseCopy, body: unsendableBody } : baseCopy;
  const [inlineError, setInlineError] = useState('');
  const [busy, setBusy] = useState(false);
  const primaryRef = useRef(null);
  // Present only on the UNSENDABLE tier when merge3 found conflicts (§9): the
  // merged document with inline pickers renders in place of the export-first
  // primary flow, but the export/discard secondaries stay as the safety net.
  const hasConflicts = state === RECOVERY_STATES.UNSENDABLE && Boolean(recovery?.merge?.conflicts?.length);

  // WebUI #19 (owner live report 2026-08-13): this used to be
  // `useEffect(() => { primaryRef.current?.focus(); }, [state])` — it refired
  // on EVERY `state` change, not just this panel's first appearance. With
  // authority flapping (#18) retriggering `recovery.state` every few seconds,
  // the browser yanked focus (and scrolled the primary button into view) out
  // of the editor mid-click/mid-typing, over and over, jumping the page to
  // the bottom. Two owner-mandated guards fix this:
  //   (1) focus only once per mount — the panel's first appearance — never on
  //       a later `state` change while the same instance stays mounted;
  //   (2) never steal focus at all while the editing surface already holds
  //       the user's focus/caret (covers repeat MOUNTS too: #18 flapping
  //       unmounts/remounts this panel every few seconds, and each fresh
  //       mount must still defer to an actively-typing user).
  // preventScroll stops the remaining case (a genuine first appearance while
  // nothing else is focused) from auto-scrolling the browser to the button.
  const focusedOnceRef = useRef(false);
  useEffect(() => {
    if (focusedOnceRef.current) return;
    focusedOnceRef.current = true;
    const active = document.activeElement;
    const editingSurfaceFocused = Boolean(active?.closest?.('.mc-continuous-canvas-input, [contenteditable="true"]'));
    if (editingSurfaceFocused) return;
    primaryRef.current?.focus({ preventScroll: true });
  }, []);

  // Every compound path confirms the copy step first; only a confirmed copy
  // may be followed by the destructive continuation.
  const guarded = useCallback((makeCopy, andThen) => async () => {
    setBusy(true); setInlineError('');
    try { await makeCopy(); } catch { setInlineError(EXPORT_FAILED_MESSAGE); setBusy(false); return; }
    try { await andThen(); } finally { setBusy(false); }
  }, []);

  const exportCopy = useCallback(() => exportText(recoveryText, routineId), [exportText, recoveryText, routineId]);
  const clipboardCopy = useCallback(() => copyText(recoveryText), [copyText, recoveryText]);

  const actions = {
    [RECOVERY_STATES.RECONNECTING]: {
      primary: () => onRetryNow(),
      options: [guarded(exportCopy, () => reloadPage())],
      escape: null,
    },
    [RECOVERY_STATES.UNSENDABLE]: {
      // #12 DATA LOSS FIX / MCRO001-F0050 / T-MCRO-050 (D004 §9, Case J): the
      // primary is labelled "Merge with the latest" and its own helper copy
      // promises "we'll download a copy of your edits first, then merge them
      // with the latest version". It must actually attempt that merge — a
      // fresh resync, which (useChoreographyCollaboration.js) tries
      // recoverWithMerge before any panel would reopen. Previously this was
      // guarded(exportCopy, () => onDiscard()): export a copy, then silently
      // DISCARD the edits — functionally identical to "Just show me the
      // latest version" below, just mislabelled. A merge that genuinely
      // cannot proceed does NOT fall through to a silent discard here: the
      // hook either shows the conflict picker (recovery.merge populated,
      // hasConflicts above) or leaves this same panel open, so discarding
      // stays an explicit, separate, clearly-labelled user choice.
      primary: guarded(exportCopy, () => onAttemptMerge()),
      options: [guarded(clipboardCopy, () => onDiscard()), () => onDiscard()],
      // Least-destructive secondary per the approved design: "Just show me the
      // latest version" — an explicit choice, never a silent close.
      escape: () => onDiscard(),
    },
    [RECOVERY_STATES.REJECTED]: {
      primary: guarded(exportCopy, () => reloadPage()),
      options: [guarded(clipboardCopy, () => reloadPage()), () => onDiscard()],
      // Least-destructive secondary per the approved design: "Discard that
      // change and keep going".
      escape: () => onDiscard(),
    },
    [RECOVERY_STATES.SIGNED_OUT]: {
      primary: () => onSignIn(),
      options: [],
      escape: null, // Escape is disabled: signing in again is the only path.
    },
    // T-MCRO-066 (D004 §4.1.3): the origin itself is insecure; no local action
    // can fix it. The single honest action reloads — which succeeds the moment
    // the origin is served over HTTPS. No secondaries, no Escape.
    [RECOVERY_STATES.SECURE_CONNECTION]: {
      primary: () => reloadPage(),
      options: [],
      escape: null,
    },
  }[state];

  const onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault(); event.stopPropagation();
    if (actions.escape && !busy) actions.escape();
  };

  return <div
    className="mc-collab-recovery mc-collab-recovery-guided"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="mc-recovery-title"
    aria-describedby="mc-recovery-body"
    onKeyDown={onKeyDown}
  >
    <strong id="mc-recovery-title">{copy.title}</strong>
    <p id="mc-recovery-body">{copy.body}</p>
    {hasConflicts
      ? <MergeResolution
        mergedSource={recovery.mergedSource || recoveryText}
        readOnly={readOnly}
        mintId={mintId}
        onResolved={(text) => onResolveMerge(text)}
      />
      : <button type="button" ref={primaryRef} disabled={busy} onClick={actions.primary}>{copy.primary}</button>}
    {copy.helper && !hasConflicts && <p className="mc-collab-recovery-helper">{copy.helper}</p>}
    {inlineError && <p className="mc-collab-recovery-error" role="alert">{inlineError}</p>}
    {copy.options.length > 0 && <details className="mc-collab-recovery-options">
      <summary>{MORE_OPTIONS_LABEL}</summary>
      <div>
        {copy.options.map((label, index) => <button
          key={label}
          type="button"
          disabled={busy}
          onClick={actions.options[index]}
        >{label}</button>)}
      </div>
    </details>}
  </div>;
}
